import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { isTaskLine } from "@/lib/billing-calculations";
import { breakdownAssignmentsSelect } from "@/lib/billing-query";
import { resolveBillingAttributionDate } from "@/lib/billing-attribution";
import { osloMonthKey } from "@/lib/oslo-time";

export interface BillingBreakdownRow {
  budgetLineId: string;
  projectCode: string | null;
  projectName: string;
  task: string;
  assignedHours: number;
  taskStatus: string;
  attributedAt: string;
  isZeroHours: boolean;
}

export interface BillingBreakdown {
  rows: BillingBreakdownRow[];
  totalAssignedHours: number;
}

/**
 * VIB-896: per-oppgave-grunnlaget for én ansatts faktureringsgrad i én måned.
 * Speiler attribusjonen i accumulateBillableByUserMonth, inkludert overgangen
 * til approved_at 1. september 2026. Summen av rader == månedens billableHours.
 * Inkluderer 0-time-oppgaver så "hvorfor er graden lav"-spørsmålet blir synlig.
 *
 * @param month 1-12
 */
export function useBillingRateBreakdown(
  userId: string | null,
  year: number,
  month: number | null,
) {
  return useQuery({
    enabled: !!userId && month != null,
    queryKey: ["billing-rate-breakdown", userId, year, month],
    queryFn: async (): Promise<BillingBreakdown> => {
      const { data: assignments, error: aErr } = await supabase
        .from("budget_line_assignments")
        .select(breakdownAssignmentsSelect)
        .eq("user_id", userId!)
        .in("task_status", ["completed", "approved"])
        .is("budget_lines.projects.deleted_at", null)
        .neq("budget_lines.projects.project_status", "cancelled");
      if (aErr) throw aErr;

      // Paginate: per-user year of time_entries grows past the 1000-row cap
      // (busiest user is at ~870 already). See VIB-1249.
      const timeEntries = await fetchAllPaginated<{
        budget_line_id: string | null;
        start_date: string;
      }>("time_entries", "budget_line_id, start_date", (q) =>
        q
          .eq("user_id", userId!)
          .is("deleted_at", null)
          .gte("start_date", `${year}-01-01`)
          .lt("start_date", `${year + 1}-01-01`),
      );

      const lastEntry = new Map<string, string>();
      for (const te of timeEntries) {
        if (!te.budget_line_id) continue;
        const ex = lastEntry.get(te.budget_line_id);
        if (!ex || te.start_date > ex) lastEntry.set(te.budget_line_id, te.start_date);
      }

      const rows: BillingBreakdownRow[] = [];
      let total = 0;
      const asOf = new Date();
      // Supabase typer !inner to-one-embeds som array; runtime er objekt.
      for (const a of (assignments ?? []) as unknown as Array<{
        hours: number | null; task_status: string; budget_line_id: string;
        completed_at: string | null;
        approved_at: string | null;
        budget_lines: {
          description: string | null; cost_type: string | null; quantity_hours: number | null;
          projects: { project_code: string | null; name: string | null } | null;
        } | null;
      }>) {
        const bl = a.budget_lines;
        if (bl && !isTaskLine(bl)) continue;

        const attribution = resolveBillingAttributionDate(
          a,
          lastEntry.get(a.budget_line_id) ?? null,
          asOf,
        );
        if (!attribution) continue;
        // Oslo-måned (matcher RPC/KPI-kort), ikke lokal/UTC.
        const { year: attrYear, month: attrMonth } = osloMonthKey(attribution);
        if (attrYear !== year || attrMonth !== month) continue;

        const hours = Number(a.hours ?? 0);
        total += hours;
        rows.push({
          budgetLineId: a.budget_line_id,
          projectCode: bl?.projects?.project_code ?? null,
          projectName: bl?.projects?.name ?? "Ukjent prosjekt",
          task: bl?.description ?? "Uten beskrivelse",
          assignedHours: hours,
          taskStatus: a.task_status,
          attributedAt: attribution,
          isZeroHours: hours === 0,
        });
      }

      rows.sort((x, y) => y.assignedHours - x.assignedHours);
      return { rows, totalAssignedHours: Math.round(total * 10) / 10 };
    },
    staleTime: 5 * 60 * 1000,
  });
}
